/* lib/api/ttk.js — REST-клиент /api/ttk/* (порт из frontend/src/lib/api/ttk.js, 1:1).
 * Все ответы — {ok,status,data}. Server-authoritative: мутации строк возвращают свежий
 * TTKDetailSerializer после пересчёта calculated_json. */
import { QUIET_POLL, apiFetch } from './client.js';

// kitchen-ux: category — имя группы или TTK_CATEGORY_NONE («без категории»);
// include: 'rows' — лёгкий состав у каждой ТТК (раскрытие строки таблицы);
// pos_linked: '0' — только ТТК без связи с позицией кассы («Не в кассе»), '1' — только со
// связью; не задан — все (pos-base-rework, contracts/ttk-lists.json).
// req — опции запроса (kitchen-ux C2: pinWorkspace(meta.workspaceId) таблицы реестра).
export const TTK_CATEGORY_NONE = '__none__';
export const listTTK = (params = {}, req) => {
  const query = new URLSearchParams();
  if (params.kind) query.set('kind', params.kind);
  if (params.q) query.set('q', params.q);
  if (params.status) query.set('status', params.status);
  if (params.allergen) query.set('allergen', params.allergen);
  if (params.category) query.set('category', params.category);
  if (params.sort) query.set('sort', params.sort);
  if (params.include) {
    query.set('include', Array.isArray(params.include) ? params.include.join(',') : params.include);
  }
  if (params.pos_linked != null && params.pos_linked !== '') query.set('pos_linked', String(params.pos_linked));
  const qs = query.toString();
  return apiFetch(`/api/ttk/${qs ? `?${qs}` : ''}`, req);
};

// Подсказки категорий пространства → [{name, count}] (kind: 'dish'|'semiproduct'|—).
export const listTTKCategories = (kind, req) =>
  apiFetch(`/api/ttk/categories/${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`, req);

export const TTK_STATUSES = [
  { slug: 'ready', label: 'Готовые' },
  { slug: 'unbound', label: 'Без привязки' },
  { slug: 'stub', label: 'Без рецепта' },
];
export const TTK_SORTS = [
  { slug: '-updated', label: 'Сначала свежие' },
  { slug: 'updated', label: 'Сначала старые' },
  { slug: 'title', label: 'По названию (А→Я)' },
  { slug: '-title', label: 'По названию (Я→А)' },
  { slug: '-kcal', label: 'По ккал (больше→меньше)' },
  { slug: 'kcal', label: 'По ккал (меньше→больше)' },
  { slug: 'category', label: 'По категории (А→Я)' },
  { slug: '-category', label: 'По категории (Я→А)' },
];
export const TTK_KINDS = [
  { slug: 'dish', label: 'Блюда' },
  { slug: 'semiproduct', label: 'Полуфабрикаты' },
];

export const getTTKUsedIn = (id, req) => apiFetch(`/api/ttk/${id}/used-in/`, req);
export const getTTK = (id, req) => apiFetch(`/api/ttk/${id}/`, req);
export const createTTKFromMessage = (messageId) =>
  apiFetch('/api/ttk/from-message/', { method: 'POST', body: { message_id: messageId } });
export const createEmptyTTK = ({ title, kind, output_g, category } = {}, req) => {
  const body = { title, kind };
  if (output_g != null) body.output_g = output_g;
  if (category != null) body.category = category;
  return apiFetch('/api/ttk/', { method: 'POST', body, ...req });
};
export const updateTTK = (id, payload, req) =>
  apiFetch(`/api/ttk/${id}/`, { method: 'PATCH', body: payload, ...req });
export const deleteTTK = (id, req) => apiFetch(`/api/ttk/${id}/`, { method: 'DELETE', ...req });

// F41 — bulk-операции multi-select реестра.
export const bulkDeleteTTK = (ids, req) =>
  apiFetch('/api/ttk/bulk-delete/', { method: 'POST', body: { ids }, ...req });
// Копирование ТТК в другое пространство того же человека (ttk/workspace_copy.py): куда можно
// ({can_copy, targets}) и сама копия ({ttk_ids, target_workspace_id} → отчёт). Пространство-
// источник — заголовок запроса (req: pinWorkspace операции).
export const getTTKCopyTargets = (req) => apiFetch('/api/ttk/copy-targets/', req);
export const copyTTKToWorkspace = (ids, targetWorkspaceId, req) =>
  apiFetch('/api/ttk/copy-to-workspace/', {
    method: 'POST', body: { ttk_ids: ids, target_workspace_id: targetWorkspaceId }, ...req,
  });
export const buildBulkExportUrl = (ids) =>
  '/api/ttk/export-bulk.zip?ids=' + ids.join(',');

export const linkSemiproduct = (parentId, ingredientIndex, semiproductTtkId) =>
  apiFetch(`/api/ttk/${parentId}/link-semiproduct/`, {
    method: 'POST',
    body: { ingredient_index: ingredientIndex, semiproduct_ttk_id: semiproductTtkId },
  });
export const linkRowToCard = (ttkId, rowId, cardId, req) =>
  apiFetch(`/api/ttk/${ttkId}/rows/${rowId}/link-card/`, { method: 'POST', body: { card_id: cardId }, ...req });
export const createTTKFromCard = (cardId, kind = 'dish') =>
  apiFetch('/api/ttk/from-card/', { method: 'POST', body: { card_id: cardId, kind } });

export const buildExportUrl = (id, portions = 1) => {
  const p = Number(portions);
  const q = Number.isFinite(p) && p > 0 && p !== 1 ? `?portions=${p}` : '';
  return `/api/ttk/${id}/export.docx${q}`;
};
export const cloneTTK = (id, title, req) => {
  const body = title != null ? { title } : {};
  return apiFetch(`/api/ttk/${id}/clone/`, { method: 'POST', body, ...req });
};

// CRUD строк рецепта — возвращают свежий TTKDetailSerializer.data.
export const createRow = (ttkId, body) =>
  apiFetch(`/api/ttk/${ttkId}/rows/`, { method: 'POST', body });
export const createRowsBulk = (ttkId, rows, req) =>
  apiFetch(`/api/ttk/${ttkId}/rows/bulk/`, { method: 'POST', body: { rows }, ...req });
export const updateRow = (ttkId, rowId, body, req) =>
  apiFetch(`/api/ttk/${ttkId}/rows/${rowId}/`, { method: 'PATCH', body, ...req });
export const deleteRow = (ttkId, rowId, req) =>
  apiFetch(`/api/ttk/${ttkId}/rows/${rowId}/`, { method: 'DELETE', ...req });

// req — опции запроса ({headers} от pinWorkspace): создание карточки и привязка — одна
// операция, пространство закреплено на её начало (S10).
export const aiFillRowCard = (ttkId, rowId, req) =>
  apiFetch(`/api/ttk/${ttkId}/rows/${rowId}/ai-fill-card/`, { method: 'POST', ...req });
export const aiFillMissingRows = (ttkId, req) =>
  apiFetch(`/api/ttk/${ttkId}/rows/ai-fill-missing/`, { method: 'POST', ...req });

// kitchen-ux D1a (контракт D1-be): расчёт по текущему состоянию формы БЕЗ записи.
// body — lib/ttkPreview.previewRequest; req — {signal} (отмена прошлого) и/или {headers}.
// Сырой контракт {ok,status,data}: 400 {code:'cycle'|'invalid'|'too_many_rows'} разбирает
// lib/ttkPreview.previewFailure.
export const previewTTK = (body, req) =>
  apiFetch('/api/ttk/preview/', { method: 'POST', body, ...req });

// ── F40 W5: фоновая сборка ДЕРЕВА детей (tree-job) ──────────────────────────
// Единственный механизм генерации рецептов после утверждения меню/ТТК.
// Контракт payload: { status:'idle'|'pending'|'running'|'done'|'cancelled'|
//   'failed', active, total, done, failed, cancel_requested, job_id?,
//   root_entity_kind?, root_entity_id?, nodes:[{node_id,parent_id,level,title,
//   status,entity_id,error,order_index}] }
// опрос только на чтение: по умолчанию вне счёта запросов в полёте (QUIET_POLL) — не держит и
// не сбрасывает переход вкладки в пространство, выбранное в соседней; req — переопределить
export const getTreeBuildStatus = (jobId, req) =>
  apiFetch(`/api/ttk/tree/${jobId}/`, { ...QUIET_POLL, ...req });
// Статус по корню (restore после refresh: карточка знает только kind+id).
export const getTreeBuildByRoot = (rootKind, rootId) =>
  apiFetch(`/api/ttk/tree/by-root/${rootKind}/${rootId}/`);
// Ручной запуск/достройка дерева для корня (квота-гейт на беке).
export const startTreeBuild = (rootKind, rootId, ctx = {}) =>
  apiFetch('/api/ttk/tree/start/', {
    method: 'POST', body: { root_kind: rootKind, root_id: rootId, ...ctx },
  });
export const cancelTreeBuild = (jobId) =>
  apiFetch(`/api/ttk/tree/${jobId}/cancel/`, { method: 'POST' });
// «Достроить»: failed-узлы → waiting, переспавн исполнителя.
export const resumeTreeBuild = (jobId) =>
  apiFetch(`/api/ttk/tree/${jobId}/resume/`, { method: 'POST' });
